import React, {useContext, useEffect, useRef, useState} from 'react';
import SvgPriceTag from 'util/svg/createDynamicPricetag';
import 'styles/pictures/PriceTag.css';
import {fundExistingImage} from 'util/api';
import SignInPopup from 'components/login/SignInPopup';
import {UserContext} from 'components/user/UserContext';
import {usePopup} from 'popup/PopupContext';
import AddMoneyPopupWrapper from 'components/donate/AddMoneyPopupWrapper';
import {centsToString} from 'util/cashFormatter';

const PriceTag = ({amount, pictureId, onWidthChange, forShare = false}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isClicked, setIsClicked] = useState(false);
    const [inputValue, setInputValue] = useState(1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const {user} = useContext(UserContext);
    const {openPopup} = usePopup();
    const [isInputValid, setIsInputValid] = useState(false);
    const [priceTagRotation, setPriceTagRotation] = useState(0);
    const [rotationAngle, setRotationAngle] = useState(0);
    const [dashesCount, setDashesCount] = useState(0);
    const [amountClassWhole, setAmountClassWhole] = useState(0);
    const [scale, setScale] = useState(1);
    const [isLoaded, setIsLoaded] = useState(false);

    const handleMouseEnter = () => {
        if (forShare) {
            return;
        }
        setIsHovered(true);
    };
    const handleMouseLeave = () => setIsHovered(false);
    const handleClick = () => {
        if (forShare) {
            return;
        }
        if (!user.isLoggedIn) {
            openPopup(<SignInPopup/>, () => {
            }, true);
            return;
        }
        setIsClicked(true);
    };
    const handleInputChange = (e) => {
        console.log('CHANGE');
        const {value} = e.target;
        // Only update if the value is empty or contains only digits
        if (/^\d*$/.test(value)) {
            console.log(value + 'passed the test /^\\d*$/.test(value)');
            setInputValue(value);
        }
    };

    const fundImageClick = () => {
        if (!isInputValid) return;
        const cents = inputValue * 100;
        if (cents > user.balance) {
            openPopup(<AddMoneyPopupWrapper initialAmount={cents - user.balance}/>);
            return;
        }
        fundExistingImage(pictureId, cents, user.balance).then(() => {
            setIsClicked(false);
            setIsHovered(false);
        });
    };

    useEffect(() => {
        setIsInputValid(inputValue && inputValue > 0);
    }, [inputValue]);

    const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
            setIsClicked(false);
            setIsHovered(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            fundImageClick();
        } else if (e.key === 'Escape') {
            setIsClicked(false);
            setIsHovered(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isClicked && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isClicked]);

    useEffect(() => {
        const localAmountClassWhole = Math.floor(Math.log10(amount) - 1);
        const localDashesCount = Math.floor((localAmountClassWhole - 1) / 3);
        const width = isClicked ? (45 + localDashesCount * 3 + 6 * localAmountClassWhole) : (-8 + localDashesCount * 3 + 5.5 * amountClassWhole);
        const triggerWidth = (width * 2.5 + 70) * scale + 10;
        const height = isClicked ? ((width + 10) * (width + 10) / 300) : (200 / (width + 50) + 50);
        if (onWidthChange) {
            onWidthChange(triggerWidth, height);
        }
    }, [scale, isClicked]);

    useEffect(() => {
        const localAmountClass = Math.log10(amount) - 1;
        const localAmountClassWhole = Math.floor(localAmountClass);
        const localRotationAngle = Math.atan(Math.pow(localAmountClass, 3) / (4 + localAmountClass * 5)) * (180 / Math.PI);
        const localDashesCount = Math.floor((localAmountClassWhole - 1) / 3);
        const localScale = 0.7 + 0.1 * Math.pow(localAmountClass, 1.2);

        setAmountClassWhole(localAmountClassWhole);
        setDashesCount(localDashesCount);

        if (!forShare) {
            setScale(localScale);
        } else {
            setScale(0.8);
        }

        setIsLoaded(true);

        const timer = setTimeout(() => {
            setRotationAngle(localRotationAngle);
        }, 50);

        return () => clearTimeout(timer);
    }, [amount, forShare]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPriceTagRotation(rotationAngle); // Now rotationAngle is defined
        }, 10); // Give a slight delay for the animation to work
        return () => clearTimeout(timer);
    }, [rotationAngle]); // Use rotationAngle as a dependency

    if (!isLoaded) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className={`price-tag-container ${isClicked ? 'clicked' : ''} ${forShare ? 'for-share' : ''}`}
            style={{transform: `rotate(${isClicked ? 0 : priceTagRotation - 90}deg) scale(${scale})`}}
        >
            {!isClicked ? (
                <>
                    <div
                        className={forShare ? '' : 'cursor-pointer'}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onClick={handleClick}
                    >
                        <SvgPriceTag gapSize={-8 + dashesCount * 3 + 5.5 * amountClassWhole + (isHovered ? 6.5 : 0)}
                                     text={forShare ? centsToString(amount) : ''} doShadow={true}/>
                    </div>
                    <span className="price-tag-text-overlay price-tag-text">
                        {forShare ? '' : (isHovered ? '+ ' : ' ') + centsToString(amount)}
                    </span>
                </>
            ) : (
                <div className="price-tag-input-container">
                    <SvgPriceTag gapSize={45 + dashesCount * 3 + 6 * amountClassWhole} showHole={false}
                                 doShadow={true}/>
                    <div className="price-tag-inside-input-container">
                        <span className="price-tag-text">{centsToString(amount)} +</span>
                        <div className="price-tag-input-div">
                            $
                            <input
                                ref={inputRef}
                                className="price-tag-input"
                                type="text"
                                inputMode="numeric"
                                pattern="\d*"
                                value={inputValue}
                                maxLength={6}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <svg
                            style={{minWidth: '42px', height: '26px'}}
                            viewBox="0 0 38 30"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={fundImageClick}
                            className={isInputValid ? 'cursor-pointer' : ''}
                        >
                            <path d="M10 16.122H31V14.622H10V16.122Z" fill={isInputValid ? 'WHITE' : '#266F2D'}/>
                            <path
                                d="M4.01201 25.9146C3.78308 26.3223 4.1893 26.7932 4.62622 26.6265L32.7303 15.9049C33.1541 15.7433 33.1609 15.1461 32.7409 14.9748L4.40272 3.41583C3.97401 3.24096 3.5584 3.69247 3.76812 4.10527L8.85319 14.1145C9.38257 15.1565 9.35689 16.394 8.78474 17.4131L4.01201 25.9146Z"
                                stroke={isInputValid ? '#ECF5EE' : '#266F2D'}
                                strokeWidth="4"
                            />
                        </svg>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PriceTag;
